// ─────────────────────────────────────────────────────────────────────────────
// LANDMARK SMOOTHER — Phase 1 completion
// Exponential Moving Average (EMA) to eliminate landmark jitter
// Also provides velocity estimation for predictive tracking
// ─────────────────────────────────────────────────────────────────────────────

export interface Point3D { x: number; y: number; z: number; visibility?: number }

export class LandmarkSmoother {
  private alpha: number;          // EMA factor (0=frozen, 1=raw)
  private prev: Point3D[] | null = null;
  private velocity: Point3D[] | null = null;
  private velocityAlpha: number;

  /**
   * @param alpha       EMA smoothing factor 0–1 (default 0.35 = ~3-frame lag)
   * @param velocityAlpha Velocity blend factor for predictive tracking
   */
  constructor(alpha = 0.35, velocityAlpha = 0.2) {
    this.alpha = alpha;
    this.velocityAlpha = velocityAlpha;
  }

  smooth(raw: Point3D[]): Point3D[] {
    if (!this.prev || this.prev.length !== raw.length) {
      // First frame — seed with raw
      this.prev = raw.map(p => ({ ...p }));
      this.velocity = raw.map(() => ({ x: 0, y: 0, z: 0 }));
      return this.prev;
    }

    const smoothed: Point3D[] = [];
    for (let i = 0; i < raw.length; i++) {
      const r = raw[i];
      const p = this.prev[i];
      const v = this.velocity![i];

      // Confidence-weighted alpha: reduce smoothing when visibility is high
      const vis = r.visibility ?? 1;
      const a = this.alpha + (1 - this.alpha) * (1 - vis) * 0.5;

      const sx = a * r.x + (1 - a) * (p.x + v.x);
      const sy = a * r.y + (1 - a) * (p.y + v.y);
      const sz = a * r.z + (1 - a) * (p.z + v.z);

      // Update velocity
      v.x = this.velocityAlpha * (sx - p.x) + (1 - this.velocityAlpha) * v.x;
      v.y = this.velocityAlpha * (sy - p.y) + (1 - this.velocityAlpha) * v.y;
      v.z = this.velocityAlpha * (sz - p.z) + (1 - this.velocityAlpha) * v.z;

      smoothed.push({
        x: sx, y: sy, z: sz,
        visibility: r.visibility,
      });
    }

    this.prev = smoothed;
    return smoothed;
  }

  reset() {
    this.prev = null;
    this.velocity = null;
  }
}

// ── One Euro Filter — superior smoothing for fast movement ────────────────────
// Better than EMA: low lag when moving fast, high smoothing when still
export class OneEuroFilter {
  private prevFiltered: number | null = null;
  private prevDeriv = 0;
  private prevTime: number | null = null;
  private readonly minCutoff: number;
  private readonly beta: number;
  private readonly dCutoff: number;

  constructor(minCutoff = 1.0, beta = 0.007, dCutoff = 1.0) {
    this.minCutoff = minCutoff;
    this.beta = beta;
    this.dCutoff = dCutoff;
  }

  private alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(value: number, timestamp: number): number {
    if (this.prevFiltered === null || this.prevTime === null) {
      this.prevFiltered = value;
      this.prevTime = timestamp;
      return value;
    }

    const dt = Math.max((timestamp - this.prevTime) / 1000, 1e-5);
    this.prevTime = timestamp;

    // Derivative filter
    const dValue = (value - this.prevFiltered) / dt;
    const aD = this.alpha(this.dCutoff, dt);
    const dFiltered = aD * dValue + (1 - aD) * this.prevDeriv;
    this.prevDeriv = dFiltered;

    // Value filter
    const cutoff = this.minCutoff + this.beta * Math.abs(dFiltered);
    const a = this.alpha(cutoff, dt);
    this.prevFiltered = a * value + (1 - a) * this.prevFiltered;

    return this.prevFiltered;
  }

  reset() {
    this.prevFiltered = null;
    this.prevTime = null;
    this.prevDeriv = 0;
  }
}

// ── Multi-landmark 1€ smoother ─────────────────────────────────────────────
export class OneEuroLandmarkSmoother {
  private filters: Array<{ x: OneEuroFilter; y: OneEuroFilter; z: OneEuroFilter }> = [];

  constructor(
    private numLandmarks: number,
    private minCutoff = 1.0,
    private beta = 0.007
  ) {}

  smooth(landmarks: Point3D[], timestamp: number): Point3D[] {
    if (this.filters.length !== this.numLandmarks) {
      this.filters = Array.from({ length: this.numLandmarks }, () => ({
        x: new OneEuroFilter(this.minCutoff, this.beta),
        y: new OneEuroFilter(this.minCutoff, this.beta),
        z: new OneEuroFilter(this.minCutoff, this.beta),
      }));
    }

    return landmarks.map((lm, i) => ({
      x: this.filters[i].x.filter(lm.x, timestamp),
      y: this.filters[i].y.filter(lm.y, timestamp),
      z: this.filters[i].z.filter(lm.z, timestamp),
      visibility: lm.visibility,
    }));
  }

  reset() {
    this.filters = [];
  }
}
